import React, { useState, createContext, useContext, useRef } from "react";
import {
  getDocsByIds,
  getOrgAreas,
  getOrgQuickTaleDocs,
} from "../../modules/AreaManagement/hooks/AreaHook";
import { useAuth } from "../../hooks";
import { AlbumsRef, ContributorsRef, QuickTalesRef } from "../../firebase";

export const AreasContext = createContext();
export const useAreasContext = () => useContext(AreasContext);

// "All" and "Nearby" contain every eligible QT in the org (Nearby is the same
// set, just sorted by distance in the app), so their count is computed once.
const isOrgWideArea = (name) => name === "All" || name === "Nearby";

// Explicit areas: (public OR unlisted) AND approved.
// Mirrors the filter applied to the Area details table.
const isDisplayableQuickTale = (qt) => {
  const listenStatus = (qt?.status || "").toLowerCase();
  const approvalStatus = (qt?.approvalStatus?.status || "").toLowerCase();
  const isListenable = listenStatus === "public" || listenStatus === "unlisted";
  return isListenable && approvalStatus === "approved";
};

// All / Nearby: approved, finished (not draft), public or unlisted.
const isOrgWideEligibleQuickTale = (qt) =>
  qt?.isDraft !== true && isDisplayableQuickTale(qt);

export const AreaProvider = ({ children }) => {
  const { currentUser, organizationId } = useAuth();
  const [allAreas, setAllAreas] = useState([]);
  const [loading, setLoading] = useState(false);

  const [order, setOrder] = useState("asc");
  // "" = no column sort: rows show in their saved position (drag-and-drop)
  // order as returned by Firestore.
  const [orderBy, setOrderBy] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(200);

  // Incremented on every load so a slow background count from an older load
  // can't overwrite the results of a newer one.
  const loadIdRef = useRef(0);

  /**
   * Resolve "# of QT in Area" in the background using only raw quicktale /
   * album docs (no user/narrator/org lookups), batched 30 ids per Firestore
   * query and de-duplicated across all areas.
   */
  const resolveAreaQTCounts = async (areas, orgId, loadId) => {
    try {
      const needsOrgWide = areas.some((a) => isOrgWideArea(a?.name));

      // Gather album ids + direct quicktale ids across all explicit areas.
      const albumIds = [];
      const directQTIds = [];
      areas.forEach((area) => {
        if (isOrgWideArea(area?.name)) return;
        (area?.albumsAndQTs ?? []).forEach((el) => {
          if (el?.type === "albums") albumIds.push(el.id);
          else if (el?.type === "quicktales") directQTIds.push(el.id);
        });
      });

      // Albums (for their quicktale ids) + org-wide QT query, in parallel.
      const [albumMap, orgQTs] = await Promise.all([
        getDocsByIds(AlbumsRef, albumIds),
        needsOrgWide ? getOrgQuickTaleDocs(orgId) : Promise.resolve([]),
      ]);
      if (loadId !== loadIdRef.current) return;

      // Fetch each referenced quicktale once, reusing org-wide query results.
      const qtMap = new Map(orgQTs.map((qt) => [qt.id, qt]));
      const albumQTIds = [];
      albumMap.forEach((album) =>
        (album?.quicktales ?? []).forEach((id) => albumQTIds.push(id))
      );
      const missingIds = [...directQTIds, ...albumQTIds].filter(
        (id) => typeof id === "string" && !qtMap.has(id)
      );
      const fetchedQTs = await getDocsByIds(QuickTalesRef, missingIds);
      if (loadId !== loadIdRef.current) return;
      fetchedQTs.forEach((data, id) => qtMap.set(id, { id, ...data }));

      // Count per area (each QT counted once per area).
      const orgWideQTs = orgQTs.filter(isOrgWideEligibleQuickTale);
      const counts = new Map();
      areas.forEach((area) => {
        if (isOrgWideArea(area?.name)) {
          counts.set(area.id, orgWideQTs);
          return;
        }
        const ids = new Set();
        (area?.albumsAndQTs ?? []).forEach((el) => {
          if (el?.type === "albums") {
            (albumMap.get(el.id)?.quicktales ?? []).forEach((id) =>
              ids.add(id)
            );
          } else if (el?.type === "quicktales") {
            ids.add(el.id);
          }
        });
        const qts = [];
        ids.forEach((id) => {
          const qt = qtMap.get(id);
          if (qt && isDisplayableQuickTale(qt)) qts.push(qt);
        });
        counts.set(area.id, qts);
      });

      setAllAreas((prev) =>
        prev.map((area) =>
          counts.has(area.id)
            ? { ...area, numberOfQTs: counts.get(area.id) }
            : area
        )
      );
    } catch (error) {
      console.error("Error resolving area QT counts:", error.message);
      if (loadId !== loadIdRef.current) return;
      // Don't leave row spinners running forever.
      setAllAreas((prev) =>
        prev.map((area) =>
          area.numberOfQTs == null ? { ...area, numberOfQTs: [] } : area
        )
      );
    }
  };

  /**
   * Load the areas table quickly (area docs + owner users, batched), then
   * resolve QT counts in the background. Rows show a spinner in the count
   * column while `numberOfQTs` is null. The returned promise resolves once
   * the table data is ready; it does NOT wait for the counts.
   */
  const handleGetAllAreas = async () => {
    const loadId = ++loadIdRef.current;
    try {
      const orgId = organizationId ? organizationId : currentUser?.organization;
      const querySnapshot = await getOrgAreas(orgId);
      const rawAreas = (querySnapshot?.docs ?? []).map((item) => ({
        ...item.data(),
        id: item.id,
      }));

      const userMap = await getDocsByIds(
        ContributorsRef,
        rawAreas.map((a) => a?.user)
      );
      if (loadId !== loadIdRef.current) return;

      // Keep previously resolved counts while refreshing (avoids spinner flash
      // after edits); they are replaced once the new counts resolve.
      const areas = rawAreas.map((area) => ({
        ...area,
        userData: userMap.get(area?.user) ?? {},
        quicktaleData: [],
        numberOfQTs: null,
      }));
      setAllAreas((prev) => {
        const prevCounts = new Map(prev.map((a) => [a.id, a.numberOfQTs]));
        return areas.map((a) => ({
          ...a,
          numberOfQTs: prevCounts.get(a.id) ?? null,
        }));
      });

      // Fire-and-forget: counts fill in as they resolve.
      resolveAreaQTCounts(areas, orgId, loadId);
    } catch (error) {
      console.error("Error fetching data from Firestore:", error.message);
      setLoading(false);
    }
  };
  // const handleGetAllAreas = async () => {
  //   try {
  //     const querySnapshot = await getAllArea();

  //     const areaPromises = querySnapshot.docs.map(async (item) => {
  //       const quicktale = item.data().quicktales;
  //       const quicktaleIds = quicktale.filter((qt) => typeof qt === "string");

  //       const quicktaleDataPromises = quicktaleIds.map(async (qtId) => {
  //         let quicktaleTemp = await getSingleQuickTale(qtId);
  //         return {
  //           ...quicktaleTemp.data(),
  //           id: qtId,
  //         };
  //       });

  //       const quicktaleData = await Promise.all(quicktaleDataPromises);
  //       return {
  //         ...item.data(),
  //         id: item.id,
  //         quicktaleData: quicktaleData,
  //       };
  //     });

  //     const temp = await Promise.all(areaPromises);
  //     setAllAreas(temp);
  //   } catch (error) {
  //     console.error("Error fetching data from Firestore:", error.message);
  //     setLoading(false);
  //   }
  // };

  return (
    <AreasContext.Provider
      value={{
        handleGetAllAreas,
        allAreas,
        setAllAreas,
        loading,
        setLoading,
        order,
        setOrder,
        orderBy,
        setOrderBy,
        page,
        setPage,
        rowsPerPage,
        setRowsPerPage,
      }}
    >
      {children}
    </AreasContext.Provider>
  );
};
